import { AwsUi } from '../../../core/runtime/src';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Dropdown } from '../../dropdown/src';
import { Spinner } from '../../spinner/src';
import SelectTrigger from './trigger';
import SelectFilter from './filter';
import SelectController from './utils/select-controller';
import { getHighlightedId } from '../../dropdown/src/utils/options';
import DropdownFooter from '../../dropdown/src/utils/dropdown-footer';
import createDeprecationWarnings from './utils/deprecation-warnings';
var deprecationWarnings = createDeprecationWarnings('awsui-select');
var lastGeneratedId = 0;
var nextGeneratedId = function () { return "awsui-select-" + lastGeneratedId++; };
export var Select = AwsUi.__addComponent('awsui-select', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            this.controller = new SelectController(this);
            if (!this.controlId) {
                this.controlId = nextGeneratedId();
            }
        },
        attached: function () {
            this.controller.attached();
        },
        detached: function () {
            this.controller.detached();
        }
    },
    regions: {
        // Displayed when there are no options.
        empty: {},
        // Displayed for `filteringType="auto"` when there are no matches for the filtering.
        noMatch: {}
    },
    properties: {
        // Whether the whole select should be disabled.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean',
            onChange: function () {
                if (this.disabled) {
                    this.controller.closeDropdown();
                }
            }
        },
        // Renders the control in invalid state.
        invalid: {
            type: 'boolean',
            nullable: true
        },
        // Specifies the current status of data fetching:
        // * `pending`: no request in progress, but there may be more items loaded.
        // * `loading`: data fetching is in progress.
        // * `finished`: pagination reached the end, no more requests expected.
        // * `error`: an error occurred during fetch. You should provide user an option to recover, see examples
        // for more details.
        statusType: {
            type: 'string',
            valid: ['pending', 'loading', 'finished', 'error'],
            defaultValue: 'finished'
        },
        // Renders the control in loading state.
        //
        // **Deprecated:** Use `statusType="loading"` instead.
        loading: {
            type: 'boolean',
            onChange: function (oldValue, newValue) {
                if (newValue) {
                    deprecationWarnings.loading();
                }
            }
        },
        // Text to be displayed when in loading state.
        loadingText: {
            type: 'string'
        },
        // Text to be displayed at the bottom of the dropdown menu once pagination has reached the end.
        finishedText: {
            type: 'string'
        },
        // Text to be displayed in case of a data fetching error. Make sure to also provide the `recoveryText`,
        // see its documentation for more details.
        errorText: {
            type: 'string'
        },
        // Text for the recovery button. Displayed next to the error text.
        // Use the `recoveryClick` event to do a recovery action, for example, retrying the request.
        recoveryText: {
            type: 'string'
        },
        // Enable filtering for dropdown options:
        // * `none`: do not display filtering input.
        // * `auto`: component will automatically filter options based on user input.
        // * `manual`: set up `delayedFilteringChange` event listener and filter items on your side or request
        // them from server
        filteringType: {
            type: 'string',
            valid: ['none', 'auto', 'manual'],
            defaultValue: 'none'
        },
        // Whether option filtering should be enabled.
        //
        // **Deprecated:** Use `filteringType="auto"` instead.
        enableFiltering: {
            type: 'boolean',
            onChange: function (oldValue, newValue) {
                if (newValue) {
                    deprecationWarnings.enableFiltering();
                }
            }
        },
        // Placeholder displayed in the filtering input if filtering is enabled.
        filteringPlaceholder: {
            type: 'string'
        },
        // Adds aria label on the built-in filtering input if filtering is enabled.
        filteringLabel: {
            type: 'string'
        },
        // Array of objects, each can represent a simple option with the following properties:
        //
        // * `label` [string]: A short title shown in the select to describe this item.
        // * `disabled` [boolean]: Whether this item is disabled.
        //
        // Extended options can provide further guidance about each option by specifying further
        // properties:
        // * `id` [string]: The id of the select when this item is selected.
        // * `labelTag` [string]: A tag giving further guidance shown pulled right next to the label.
        // * `description` [string]: Further explanatory guidance about the option below the label.
        // * `iconName` [string]: The name of the icon, use with awsui icons.
        // * `iconUrl` [string]: The url to the icon, use with custom icons.
        // * `iconAlt` [string]: The alternate text, use with custom icons.
        // * `tags` [string array]: A list of tags giving further guidance about the option.
        // * `filteringTags` [string array]: A list of metadata that is never displayed unless one of
        // the tags gives a match for the user filtering.
        //
        // Options can be grouped by specifying the given option as a group of options:
        //
        // * `label` [string]: description of the group.
        // * `options` [array]: array of simple options.
        // * `disabled` [boolean]: whether the whole group is disabled.
        //
        // Notice that only one level of options nesting is supported.
        options: {
            type: 'array',
            itemTypes: ['Select.IOption', 'Select.Option', 'Select.OptionsGroup']
        },
        // The name of the control.
        // Note this attribute will not be attached to any DOM element, but will be exposed on component attributes.
        name: {
            type: 'string'
        },
        // Hint text displayed in the field when no option has been selected.
        placeholder: {
            type: 'string'
        },
        // Currently selected option.
        // If you would like to empty the selection, provide `null` as a value (`undefined` will not work).
        selectedOption: {
            type: 'object',
            objectType: 'Select.Option'
        },
        // Id of the currently selected option.
        //
        // **Deprecated:** Use `selectedOption` instead.
        selectedId: {
            type: 'string',
            onChange: function (oldValue, newValue) {
                if (newValue) {
                    deprecationWarnings.selectedId();
                }
            }
        },
        // Defines the variant of the trigger. It can be either a simple label,
        // choosing `label`, or the entire option, by setting it to `option`.
        triggerVariant: {
            type: 'string',
            valid: ['label', 'option'],
            defaultValue: 'label'
        },
        // Id for the trigger component. It defaults to an automatically generated id.
        controlId: {
            type: 'string',
            announceValue: true
        },
        // Adds aria-labelledby on the select.
        // 1. Define a custom id inside the label of the form field
        // 2. Refer to that label using this property
        ariaLabelledby: {
            type: 'string'
        },
        // Adds aria-describedby on the select. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string',
            nullable: true
        },
        // Adds aria-required on the native input
        ariaRequired: {
            type: 'boolean'
        },
        // Adds aria-label on the select.
        label: {
            type: 'string'
        },
        // Localized string that describes an option as selected.
        // This is required to provide a good screen reader experience. See the
        // [accessibility guidelines](/system/components/awsui-select/?tabId=usage#accessibility-guidelines)
        // for more details.
        selectedLabel: {
            type: 'string'
        },
        // Optionally overrides the element that is announced to screen readers
        // when the highlighted option changes. By default, this announces
        // the option's name and properties, as well as its selected state if
        // the selectedLabel property is defined.
        // The highlighted option is provided, as well as its group (if groups
        // are used and it differs from the group of the previously highlighted option).
        //
        // See the
        // [accessibility guidelines](/system/components/awsui-select/?tabId=usage#accessibility-guidelines)
        // fore more details.
        highlightedOptionDescription: {
            type: 'function',
            functionType: 'Select.HighlightedOptionDescription'
        },
        // Current filtering input.
        __filteringText: {
            type: 'string'
        },
        // A flag showing whether the dropdown is open.
        __open: {
            type: 'boolean'
        },
        // The currently highlighted option in the dropwdown. The browser does not
        // consider it as an active element.
        __highlightedOption: {
            type: 'object',
            objectType: 'Select.Option'
        },
        // A default suggested aria-labelledby value that can be pushed down from a FormField
        __suggestedAriaLabelledby: {
            type: 'string'
        }
    },
    events: {
        // Fired when the select is blured.
        blur: {
            bubbles: false,
            cancelable: false
        },
        // Fired when the select is focused.
        focus: {
            bubbles: false,
            cancelable: false
        },
        // Fired whenever the user selects an option.
        // The event `detail` contains the current `selectedId` and `selectedOption`.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'Select.ChangeDetail'
        },
        // Fired after the filtering input state changes:
        // * When the user has changed the value and stopped typing for a certain period of time.
        // * When the user opens the dropdown menu and filtering state has been reset.
        //
        // Use this in case of `filteringType="manual"` to load filtered items
        delayedFilteringChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'Select.DelayedFilteringChangeDetail'
        },
        // Fired when `statusType` is set to `pending` and there is a need for more items:
        // * When the user has scrolled to the bottom of the dropdown.
        // * When the options property changed and there are not enough items to make the list scrollable.
        loadMore: {
            bubbles: true,
            cancelable: false
        },
        // Fired when the user clicks the recovery button placed at the bottom of the dropdown list in the error state.
        // Use this to retry a failed request or provide another option for the user to recover from the error.
        recoveryClick: {
            bubbles: true,
            cancelable: false
        }
    },
    functions: {
        // Gives focus to the element without opening it.
        focus: function () {
            this.controller.getTriggerComponent().focus();
        }
    },
    render: function (m, h) {
        var _this = this;
        if (this.loading) {
            return (AwsUi.__createElement("div", { className: "awsui-select-keyboard-area" },
                AwsUi.__createElement("div", { className: "awsui-util-status-inactive awsui-select-loading" },
                    AwsUi.__createElement(Spinner, { size: "normal" }),
                    AwsUi.__createElement("span", { className: "awsui-select-loading-text" }, this.loadingText))));
        }
        var cb = new LegacyClassBuilder(this);
        cb.addComponentClass('open', this.__open);
        var displayFilter = this.controller.shouldDisplayFilter();
        var ariaLabelledby = [this.ariaLabelledby, this.__suggestedAriaLabelledby].filter(function (el) { return el; }).join(' ');
        var dropdownControlId = this.controlId + "-dropdown";
        var selectedOption = this.controller.getSelectedOption();
        var visibleOptions = this.controller.getVisibleOptions();
        var filter = displayFilter && (AwsUi.__createElement(SelectFilter, { value: this.__filteringText, placeholder: this.filteringPlaceholder, label: this.filteringLabel, ariaLabelledby: ariaLabelledby, ariaRequired: this.ariaRequired, highlightedOption: this.__highlightedOption, ariaActivedescendant: this.__open ? getHighlightedId(dropdownControlId, visibleOptions, this.__highlightedOption) : '', ariaControls: dropdownControlId + "-options" }));
        return (AwsUi.__createElement("div", { className: "awsui-select-keyboard-area" },
            AwsUi.__createElement("div", { className: cb.toClassName() },
                AwsUi.__createElement(SelectTrigger, { selectedOption: selectedOption, placeholder: this.placeholder, disabled: this.disabled, variant: this.triggerVariant, invalid: this.invalid, controlId: this.controlId, dropdownControlId: dropdownControlId, open: this.__open, ariaDescribedby: this.ariaDescribedby, ariaLabelledby: ariaLabelledby, ariaLabel: this.label }),
                AwsUi.__createElement(Dropdown, { options: visibleOptions, highlightText: this.__filteringText, open: this.__open, selectedOptions: selectedOption ? [selectedOption] : [], highlightedOption: this.__highlightedOption, filter: filter, controlId: dropdownControlId, isMultiselect: false, hasFiltering: displayFilter, ariaRequired: this.ariaRequired, listBottom: AwsUi.__createElement(DropdownFooter, { status: this.statusType, isEmpty: visibleOptions.length === 0, emptyText: h.region(this.controller.getEmptyTextRegion(), 'span'), loadingText: this.loadingText, finishedText: this.finishedText, errorText: this.errorText, recoveryText: this.recoveryText, onRecoveryClick: function () { return _this.__fireEvent('recoveryClick'); } }), loadMore: this.controller.loadMoreHandler, selectedLabel: this.selectedLabel, highlightedOptionDescription: this.highlightedOptionDescription }))));
    },
    wrapperSupport: {
        ngModel: {
            value: 'selectedId',
            event: 'change'
        }
    }
});
//# sourceMappingURL=select.js.map